import { AuthProvider } from 'react-admin'

import { getSupabase } from './lib/supabase'
import { startAuthListener, useAuthStore } from './stores/auth-store'

export const authProvider: AuthProvider = {
  // Called when the user attempts to log in
  login: async ({ username, password }: { username: string; password: string }) => {
    const result = await useAuthStore.getState().signInWithPassword({
      email: username,
      password: password,
    })

    if (result._tag === 'Left') {
      throw new Error(result.left.message)
    }

    // No need to manually store the session as Supabase handles this
    return Promise.resolve()
  },

  // Called when the user clicks on the logout button
  logout: async () => {
    await useAuthStore.getState().signOut()
    return Promise.resolve()
  },

  // Called when the API returns an error
  checkError: ({ status }: { status: number }) => {
    if (status === 401 || status === 403) {
      // Let Supabase handle the session cleanup
      void getSupabase().auth.signOut()
      return Promise.reject(new Error('Unauthorized'))
    }
    return Promise.resolve()
  },

  // Called when the user navigates to a new location, to check for authentication
  checkAuth: async () => {
    startAuthListener()
    // Get the current session state from Supabase
    const { data } = await getSupabase().auth.getSession()
    if (data.session) {
      return Promise.resolve()
    }
    return Promise.reject(new Error('No active session'))
  },

  // Called when the user navigates to a new location, to check for permissions / roles
  getPermissions: async () => {
    const { data } = await getSupabase().auth.getSession()
    return Promise.resolve(data.session?.user.role ?? '')
  },

  // Get user's identity
  getIdentity: async () => {
    const { data } = await getSupabase().auth.getSession()
    if (!data.session?.user) {
      return Promise.reject(new Error('No active session'))
    }
    return Promise.resolve({
      id: data.session.user.id,
      fullName: data.session.user.email ?? '',
      avatar: undefined,
    })
  },
}
